import Moment from 'moment';
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Ref, Watch } from 'vue-property-decorator';

@Component({})
class EdDateTimePickerComponent extends Vue {

  /* In */
  @Prop() readonly label: string;
  @Prop() readonly disabled: boolean;
  @Prop() readonly placeholder: string;
  @Prop({ default: () => [] }) validations: ((value: string) => string | boolean)[];;

  /* Out */
  public readonly onChange: string = 'onChange';

  /* Data */
  public value: string = '';
  public timeValue: string = '';
  public selectedDate: string = '';
  public selectedTime: string = '';
  public isCalendarVisible: boolean = false; 
  public calendarPosition: { top: string | 'auto'; bottom: string | 'auto'; left: string } = { top: '100%', bottom: 'auto', left: '0px' };
  public DEFAULT_CALENDAR_MODE = 'month';
  public error: string = null; 
  public activeView: string = 'calendar'; 
  public enableTime: boolean = true; 
  public focusSelector: string = null; 

  @Ref('dateInput')readonly  dateInput!: HTMLInputElement | null;
  @Ref('calendarContainer')readonly calendarContainer!: HTMLElement | null; 

  get borderColor() {
    return 'var(--ed-color-green)';
  }

  get errorText() {
    return this.error;
  }

  get disclamerStyle() {
    return {
      color: 'var(--ed-color-danger)'
    }
  }

  get model() {
    return {value: this.date};
  }

  set model(_) {}

  get date() {
    let format = 'YYYY-MM-DD h:mm A';
    let dateTime = `${this.selectedDate} ${this.selectedTime}`;
    let result = '';
    
    if (this.selectedDate) {
      const momentDate = Moment(dateTime, 'YYYY-MM-DD h:mm A');
      if (momentDate.isValid()) {
        result = momentDate.format(format);
      }
    }
  
    return result;
  }

  get datePickerClass() {
    return {
      'ed-date-picker--disabled': this.disabled
    }
  }

  /* Methods */
  created() {
    this.setValue();
    this.updateValue();
  }

  mounted() {
    this.$nextTick(() => {
      document.addEventListener('click', this.onClickOutside.bind(this));
    });
  }

  beforeDestroy() { 
    document.removeEventListener('click', this.onClickOutside.bind(this));
  }
    
  convertToDate(value: any): Date | null {
    if (value == null) return null;
    
    if (typeof value === 'string') {
      const momentDate = Moment(value);
      if (momentDate.isValid()) {
        return momentDate.toDate();
      }
    }
    
    if (value instanceof Date || typeof value === 'number') {
      const date = new Date(value);
      return isNaN(date.getTime()) ? null : date;
    }
    
    return null;
  }

  setValue() {
    let attrs: any = this.$attrs;
    let date = this.convertToDate(attrs.value);
    if (date) {
      this.enableTime = true;
      this.value = Moment(date).format('YYYY-MM-DD');
      this.timeValue = Moment(date).format('h:mm A');
      this.selectedDate = this.value;
      this.selectedTime = this.timeValue;
    } else {
      this.enableTime = false;
    }
    this._checkValidations();
  }

  updateValue() {
    let dateTime = `${this.selectedDate} ${this.selectedTime}`;
    let date;
    if (dateTime.trim().length > 0) {
      const momentDate = Moment(dateTime, 'YYYY-MM-DD h:mm A');
      if (momentDate.isValid()) {
        date = momentDate.toDate();
      }
    }
    this.$emit('input', date);  
  }

  onChangeDate(dateTime) {
    let date;
    if (dateTime.trim().length > 0) {
      const momentDate = Moment(dateTime, 'YYYY-MM-DD h:mm A');
      if (momentDate.isValid()) {
        date = momentDate.toDate();
      }
    }
    this.$emit(this.onChange, date);
    this.updateValue();
  }

  onClickClean() {
    if (!this.disabled) {
      this.value = '';
      this.timeValue = '';
      this.selectedDate = '';
      this.selectedTime = '';
      this.onChangeDate('');
      this.isCalendarVisible = false;
    }
  }

  openCalendar() {
    if (!this.disabled) {
      this.value = this.selectedDate;
      this.isCalendarVisible = true;
    }
  }

  adjustCalendarPosition() {
    if (!this.dateInput ) {
      return;
    }
    const parentElement = this.$parent.$el;
    const parentRect = parentElement.getBoundingClientRect();
    const inputRect = this.dateInput.getBoundingClientRect();
    const spaceBelow = parentRect.bottom - inputRect.bottom;
    const spaceRight = parentRect.right - inputRect.right;
    const calendarWidth = 370;
    const calendarHeight = 400;

  
    const newPosition: { top: string | 'auto'; bottom: string | 'auto'; left: string; right: string | 'auto'} = {
      top: '100%',
      bottom: 'auto',
      left: '0px',
      right: 'auto'
    };

    if (spaceBelow < calendarHeight) {
      newPosition.top = 'auto';
      newPosition.bottom = `${-10-spaceBelow}px`;
    } 

     if (spaceRight < calendarWidth) { 
       newPosition.left = 'auto';
      newPosition.right = `${10-spaceRight}px`;
     }

    this.calendarPosition = newPosition; 
  }

  onDateTimeChanged() {
    if (this.value) {
      const momentDate = Moment(this.value, 'YYYY-MM-DD');
      if (momentDate.isValid()) {
        this.selectedDate = momentDate.format('YYYY-MM-DD');
      }
    } else {
      this.selectedDate = '';
    }

    if (this.timeValue) {
      const momentTime = Moment(this.timeValue, 'h:mm A');
      if (momentTime.isValid()) {
        this.selectedTime = momentTime.format('h:mm A');
      }
    } else {
      this.selectedTime = '';
    }

    this.onChangeDate(`${this.selectedDate} ${this.selectedTime}`);
    this._checkValidations();
  }
  
  onClickOk() {
    this.isCalendarVisible = false;
  }

  private _checkValidations() {
    if (this.validations) {
      let dateTime = `${this.selectedDate} ${this.selectedTime}`;
      let dateTimeFormated = '';
      
      if (dateTime.trim().length > 0) {
        const momentDate = Moment(dateTime, 'YYYY-MM-DD h:mm A');
        if (momentDate.isValid()) {
          dateTimeFormated = momentDate.format('YYYY-MM-DD h:mm A');
        }
      }

      let error = null;
      this.validations.forEach(fn => {
        let result = fn(dateTimeFormated);
        if (typeof result === 'string') {
          error = result;
        }
      });
      this.error = error;
    }
  }

  onCalendarChange(e) {
    const momentDate = Moment(e.detail.value, 'YYYY-MM-DD');
    if (momentDate.isValid()) {
      this.value = momentDate.format('YYYY-MM-DD');
      this.onDateTimeChanged();
      this.activeView = 'time';
    }
  }

  onTimeChange(e) {
    this.focusSelector = e.detail.value.focusSelector;
    const momentTime = Moment(e.detail.value.formatted, 'h:mm A');
    if (momentTime.isValid()) {
      this.timeValue = momentTime.format('h:mm A');
      this.onDateTimeChanged();
    }
  }

  onViewChange(e) {
    this.activeView = e.detail.value.view;
  }

  onClickOutside(event) {
    this.$nextTick(() => {
      const target = event.target;
      const calendarContainer = this.$el.querySelector('.calendar-container');
  
      if (this.isCalendarVisible && calendarContainer && !calendarContainer.contains(target) && !this.$el.contains(target)) {
        this.isCalendarVisible = false;
        this.value = '';
      }
    });
  }

  @Watch('$attrs')
  onAttrsChanged() {
    setTimeout(() => {
      this.setValue();
    }, 5);
  }

  @Watch('isCalendarVisible')
  onIsCalendarVisibleChanged(newValue: boolean) {
    if(newValue == true){
      this.$nextTick(() => {    
        this.adjustCalendarPosition();
      });
    } else {
      this.activeView = 'calendar';
    }
  }

}

export default Vue.component('ed-datetime-picker', EdDateTimePickerComponent);